(() => {
    if (window.__g3ScheduleVisitReady === true) return;

    const modalId = "schedule-visit-modal";
    const dayMs = 24 * 60 * 60 * 1000;
    const maxAdvanceDays = 60;
    const slotHours = Array.from({ length: 8 }, (_, index) => 10 + index);
    let state = {
        selectedDate: null,
        selectedTime: "",
        visibleMonth: null,
        lastTrigger: null,
        startedAt: Date.now(),
    };
    let autoCloseTimerId = null;

    const pad2 = (value) => String(value).padStart(2, "0");
    const today = () => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    };
    const addDays = (date, days) => {
        const next = new Date(date);
        next.setDate(next.getDate() + days);
        return next;
    };
    const dateKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
    const parseDateKey = (value) => {
        const [year, month, day] = String(value || "").split("-").map((part) => Number.parseInt(part, 10));
        if (!year || !month || !day) return null;
        return new Date(year, month - 1, day);
    };
    const sameDay = (a, b) => Boolean(a && b && dateKey(a) === dateKey(b));
    const formatDate = (date, options) => new Intl.DateTimeFormat("en-IN", options).format(date);
    const formatSlotHour = (hour) => {
        const labelHour = hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? "PM" : "AM";
        return `${labelHour}:00 ${period}`;
    };
    const formatSlotLabel = (hour) => `${formatSlotHour(hour)} - ${formatSlotHour(hour + 1)}`;
    const isPastDate = (date) => date < today();
    const isBeyondRange = (date) => date > addDays(today(), maxAdvanceDays);
    const isSlotAvailable = (date, hour) => {
        if (!date || isPastDate(date) || isBeyondRange(date)) return false;
        const slotDate = new Date(date);
        slotDate.setHours(hour, 0, 0, 0);
        return slotDate > new Date();
    };
    const firstAvailableDate = () => {
        for (let offset = 0; offset <= maxAdvanceDays; offset += 1) {
            const date = addDays(today(), offset);
            if (slotHours.some((hour) => isSlotAvailable(date, hour))) {
                return date;
            }
        }
        return today();
    };
    const firstAvailableTime = (date) => {
        const hour = slotHours.find((slotHour) => isSlotAvailable(date, slotHour));
        return Number.isFinite(hour) ? `${pad2(hour)}:00` : "";
    };
    const readCsrfToken = (scope = document) => {
        const scoped = scope?.querySelector?.('input[name="csrfmiddlewaretoken"]');
        const scopedValue = String(scoped?.value || "").trim();
        if (scopedValue && scopedValue !== "NOTPROVIDED") return scopedValue;
        const fromHelper = String(window.g3GetCsrfToken?.(scope instanceof HTMLFormElement ? scope : null) || "").trim();
        if (fromHelper) return fromHelper;
        const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
        return match && match[1] ? decodeURIComponent(match[1]) : "";
    };
    const setStatus = (form, type, message) => {
        let status = form.querySelector(".schedule-visit-status");
        if (!status) {
            status = document.createElement("div");
            status.className = "schedule-visit-status";
            status.setAttribute("role", "status");
            status.setAttribute("aria-live", "polite");
            form.insertBefore(status, form.querySelector("label") || form.firstElementChild);
        }
        status.classList.toggle("is-error", type === "error");
        status.classList.toggle("is-success", type === "success");
        status.textContent = message;
    };
    const clearStatus = (form) => {
        form?.querySelector(".schedule-visit-status")?.remove();
        form?.querySelectorAll("[aria-invalid='true']").forEach((field) => {
            field.removeAttribute("aria-invalid");
        });
    };
    const clearAutoClose = () => {
        if (!autoCloseTimerId) return;
        window.clearTimeout(autoCloseTimerId);
        autoCloseTimerId = null;
    };
    const scheduleAutoClose = (delayMs = 5000) => {
        clearAutoClose();
        autoCloseTimerId = window.setTimeout(() => {
            autoCloseTimerId = null;
            close();
        }, delayMs);
    };
    const markInvalid = (form, name) => {
        const field = form.querySelector(`[name="${name}"]`);
        field?.setAttribute("aria-invalid", "true");
        field?.focus?.();
    };
    const setLoading = (form, loading) => {
        const button = form.querySelector("[data-schedule-submit]");
        if (!button) return;
        button.disabled = loading;
        button.textContent = loading ? "Scheduling..." : "Submit Visit Request";
    };
    const track = (name, node, metadata = {}) => {
        if (typeof window.g3TrackAnalyticsEvent !== "function") return;
        window.g3TrackAnalyticsEvent({
            name,
            entity_type: "",
            entity_id: null,
            metadata: {
                source: "schedule-visit",
                ...metadata,
            },
        });
    };

    const buildModal = () => {
        const existing = document.getElementById(modalId);
        if (existing) return existing;

        const modal = document.createElement("div");
        modal.id = modalId;
        modal.className = "schedule-visit-modal";
        modal.hidden = true;
        modal.setAttribute("aria-hidden", "true");
        modal.innerHTML = `
            <div class="schedule-visit-dialog" role="dialog" aria-modal="true" aria-labelledby="schedule-visit-heading">
                <button class="schedule-visit-close" type="button" aria-label="Close schedule visit">&times;</button>
                <div class="schedule-visit-shell">
                    <section class="schedule-visit-main" aria-labelledby="schedule-visit-heading">
                        <div class="schedule-visit-head">
                            <p class="schedule-visit-kicker">Site Visit</p>
                            <h2 id="schedule-visit-heading">Schedule your visit</h2>
                        </div>
                        <div class="schedule-visit-calendar" data-schedule-calendar></div>
                        <div class="schedule-visit-slots" data-schedule-slots></div>
                    </section>
                    <aside class="schedule-visit-side">
                        <div class="schedule-visit-summary" data-schedule-summary></div>
                        <button class="schedule-visit-continue" type="button" data-schedule-continue disabled>Schedule your visit</button>
                        <form class="schedule-visit-form" data-schedule-form hidden novalidate>
                            <input type="hidden" name="date">
                            <input type="hidden" name="time">
                            <input type="hidden" name="page_url">
                            <label>
                                <span>Client Name</span>
                                <input type="text" name="name" autocomplete="name" required>
                            </label>
                            <label>
                                <span>Mobile Number</span>
                                <input type="tel" name="phone" autocomplete="tel" inputmode="tel" maxlength="20" required>
                            </label>
                            <label>
                                <span>Email</span>
                                <input type="email" name="email" autocomplete="email" required>
                            </label>
                            <label>
                                <span>Requirement</span>
                                <textarea name="comment" rows="4" placeholder="Project, locality, budget, or visit notes"></textarea>
                            </label>
                            <button class="schedule-visit-submit" type="submit" data-schedule-submit>Submit Visit Request</button>
                        </form>
                    </aside>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        window.g3EnsureConsent?.(modal.querySelector("form"));

        modal.querySelector(".schedule-visit-close")?.addEventListener("click", close);
        modal.addEventListener("click", (event) => {
            if (event.target === modal) close();
        });
        modal.querySelector("[data-schedule-continue]")?.addEventListener("click", showForm);
        modal.querySelector("[data-schedule-form]")?.addEventListener("submit", submit);
        return modal;
    };

    const renderCalendar = (modal) => {
        const calendar = modal.querySelector("[data-schedule-calendar]");
        const month = state.visibleMonth || new Date(today().getFullYear(), today().getMonth(), 1);
        const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
        const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
        const leadingDays = monthStart.getDay();
        const totalCells = Math.ceil((leadingDays + monthEnd.getDate()) / 7) * 7;
        const cells = [];

        for (let index = 0; index < totalCells; index += 1) {
            const dayNumber = index - leadingDays + 1;
            const cellDate = new Date(month.getFullYear(), month.getMonth(), dayNumber);
            const inMonth = dayNumber >= 1 && dayNumber <= monthEnd.getDate();
            const disabled = !inMonth || isPastDate(cellDate) || isBeyondRange(cellDate);
            cells.push(`
                <button
                    class="schedule-visit-day${sameDay(cellDate, state.selectedDate) ? " is-selected" : ""}"
                    type="button"
                    data-schedule-date="${dateKey(cellDate)}"
                    ${disabled ? "disabled" : ""}
                >${cellDate.getDate()}</button>
            `);
        }

        calendar.innerHTML = `
            <div class="schedule-visit-calendar-head">
                <button type="button" data-schedule-prev aria-label="Previous month">&lt;</button>
                <strong>${formatDate(monthStart, { month: "long", year: "numeric" })}</strong>
                <button type="button" data-schedule-next aria-label="Next month">&gt;</button>
            </div>
            <div class="schedule-visit-weekdays" aria-hidden="true">
                <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
            </div>
            <div class="schedule-visit-days">${cells.join("")}</div>
        `;

        const prevMonth = new Date(month.getFullYear(), month.getMonth() - 1, 1);
        const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
        const minMonth = new Date(today().getFullYear(), today().getMonth(), 1);
        const maxMonth = new Date(addDays(today(), maxAdvanceDays).getFullYear(), addDays(today(), maxAdvanceDays).getMonth(), 1);
        const prevButton = calendar.querySelector("[data-schedule-prev]");
        const nextButton = calendar.querySelector("[data-schedule-next]");
        prevButton.disabled = prevMonth < minMonth;
        nextButton.disabled = nextMonth > maxMonth;
        prevButton.addEventListener("click", () => {
            state.visibleMonth = prevMonth;
            render(modal);
        });
        nextButton.addEventListener("click", () => {
            state.visibleMonth = nextMonth;
            render(modal);
        });
        calendar.querySelectorAll("[data-schedule-date]").forEach((button) => {
            button.addEventListener("click", () => {
                state.selectedDate = parseDateKey(button.dataset.scheduleDate);
                state.selectedTime = "";
                modal.querySelector("[data-schedule-form]").hidden = true;
                render(modal);
            });
        });
    };

    const renderSlots = (modal) => {
        const slots = modal.querySelector("[data-schedule-slots]");
        const selectedDate = state.selectedDate || firstAvailableDate();
        state.selectedDate = selectedDate;
        const slotButtons = slotHours.map((hour) => {
            const value = `${pad2(hour)}:00`;
            const available = isSlotAvailable(selectedDate, hour);
            return `
                <button
                    class="schedule-visit-slot${state.selectedTime === value ? " is-selected" : ""}"
                    type="button"
                    data-schedule-time="${value}"
                    ${available ? "" : "disabled"}
                >
                    <span>${formatSlotLabel(hour)}</span>
                </button>
            `;
        }).join("");

        slots.innerHTML = `
            <div class="schedule-visit-slots-head">
                <span>Available slots</span>
                <strong>${formatDate(selectedDate, { weekday: "short", day: "2-digit", month: "short" })}</strong>
            </div>
            <div class="schedule-visit-slot-grid">${slotButtons}</div>
        `;
        slots.querySelectorAll("[data-schedule-time]").forEach((button) => {
            button.addEventListener("click", () => {
                state.selectedTime = button.dataset.scheduleTime || "";
                modal.querySelector("[data-schedule-form]").hidden = true;
                render(modal);
            });
        });
    };

    const renderSummary = (modal) => {
        const summary = modal.querySelector("[data-schedule-summary]");
        const continueButton = modal.querySelector("[data-schedule-continue]");
        const selectedHour = Number.parseInt(String(state.selectedTime || "").split(":")[0] || "", 10);
        summary.innerHTML = `
            <span>Selected visit</span>
            <strong>${state.selectedDate ? formatDate(state.selectedDate, { weekday: "long", day: "2-digit", month: "long" }) : "Select a date"}</strong>
            <em>${state.selectedTime ? formatSlotLabel(selectedHour) : "Select a time slot"}</em>
        `;
        continueButton.disabled = !state.selectedDate || !state.selectedTime;
    };

    const render = (modal) => {
        renderCalendar(modal);
        renderSlots(modal);
        renderSummary(modal);
    };

    const showForm = () => {
        const modal = buildModal();
        const form = modal.querySelector("[data-schedule-form]");
        if (!form || !state.selectedDate || !state.selectedTime) return;
        clearAutoClose();
        form.hidden = false;
        form.elements.date.value = dateKey(state.selectedDate);
        form.elements.time.value = state.selectedTime;
        form.elements.page_url.value = window.location.href;
        clearStatus(form);
        form.elements.name.focus();
    };

    const open = (trigger) => {
        const modal = buildModal();
        clearAutoClose();
        const selectedDate = firstAvailableDate();
        state = {
            selectedDate,
            selectedTime: firstAvailableTime(selectedDate),
            visibleMonth: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
            lastTrigger: trigger instanceof HTMLElement ? trigger : null,
            startedAt: Date.now(),
        };
        modal.dataset.endpoint = String(trigger?.dataset?.scheduleVisitEndpoint || "/schedule-visit/submit/").trim();
        modal.hidden = false;
        modal.classList.add("active");
        modal.setAttribute("aria-hidden", "false");
        document.body.classList.add("schedule-visit-open");
        render(modal);
        showForm();
        track("schedule_visit_open", trigger, { trigger_scope: "navbar" });
    };
    window.g3OpenScheduleVisit = open;

    function close() {
        const modal = document.getElementById(modalId);
        if (!modal) return;
        clearAutoClose();
        modal.classList.remove("active");
        modal.hidden = true;
        modal.setAttribute("aria-hidden", "true");
        document.body.classList.remove("schedule-visit-open");
        const restoreTarget = state.lastTrigger && document.contains(state.lastTrigger) ? state.lastTrigger : null;
        restoreTarget?.focus?.();
    }

    async function submit(event) {
        event.preventDefault();
        const form = event.currentTarget;
        const modal = document.getElementById(modalId);
        if (!(form instanceof HTMLFormElement) || !modal) return;
        clearStatus(form);
        const required = [
            ["name", "Please enter client name."],
            ["phone", "Please enter mobile number."],
            ["email", "Please enter email."],
        ];
        for (const [name, message] of required) {
            if (!String(form.elements[name]?.value || "").trim()) {
                markInvalid(form, name);
                setStatus(form, "error", message);
                return;
            }
        }

        const bootstrap = await window.g3LoadSecurityBootstrap?.();
        const csrfToken = String(bootstrap?.csrfToken || readCsrfToken(form) || "").trim();
        const leadGuardToken = String(bootstrap?.leadGuardToken || window.g3GetLeadGuardToken?.(form) || "").trim();
        if (!csrfToken) {
            setStatus(form, "error", "Security token missing. Please refresh the page and try again.");
            return;
        }
        if (!leadGuardToken) {
            setStatus(form, "error", "Security check missing. Please refresh the page and try again.");
            return;
        }

        const payload = {
            name: String(form.elements.name.value || "").trim(),
            phone: String(form.elements.phone.value || "").trim(),
            email: String(form.elements.email.value || "").trim(),
            comment: String(form.elements.comment.value || "").trim(),
            date: String(form.elements.date.value || "").trim(),
            time: String(form.elements.time.value || "").trim(),
            page_url: window.location.href,
            lead_guard_token: leadGuardToken,
            lead_started_at: String(state.startedAt || Date.now()),
            contact_hp: "",
        };
        const endpoint = modal.dataset.endpoint || "/schedule-visit/submit/";
        setLoading(form, true);
        track("schedule_visit_submit_click", form);
        try {
            const response = await (window.g3Fetch || fetch)(endpoint, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify(payload),
            });
            const data = await response.clone().json().catch(() => ({}));
            if (!response.ok || data?.ok === false) {
                throw new Error(String(data?.error || "Appointment could not be booked right now.").trim());
            }
            const bookedMessage = String(data?.message || "Visit booked successfully. Our team will contact you shortly.").trim();
            setStatus(form, "success", `${bookedMessage} This popup will close automatically in 5 seconds.`);
            track("schedule_visit_booked", form, { appointment: data?.appointment || null });
            form.reset();
            form.elements.date.value = payload.date;
            form.elements.time.value = payload.time;
            scheduleAutoClose(5000);
        } catch (error) {
            setStatus(form, "error", error?.message || "Appointment could not be booked right now.");
        } finally {
            setLoading(form, false);
        }
    }

    document.addEventListener("click", (event) => {
        const trigger = event.target instanceof Element ? event.target.closest("[data-schedule-visit-trigger]") : null;
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        open(trigger);
    }, true);
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") close();
    });

    window.__g3ScheduleVisitReady = true;
})();
